<template>
    <div class="ai-bubble" :class="'ai-bubble-' + message.kind">

        <!-- User message -->
        <div v-if="message.kind === 'user'" class="ai-bubble-user">
            <div class="ai-bubble-content">
                <img
                    v-for="src in attachedImages"
                    :key="src"
                    :src="src"
                    class="ai-bubble-image"
                    alt="attached artwork photo"
                />
                <template v-if="contentWithoutImageMarkers">{{ contentWithoutImageMarkers }}</template>
            </div>
        </div>

        <!-- Assistant message -->
        <div v-else-if="message.kind === 'assistant'" class="ai-bubble-assistant">
            <div class="ai-bubble-avatar">
                <i class="fa fa-magic"></i>
            </div>
            <div class="ai-bubble-body">
                <div v-if="message.content" class="ai-bubble-content">{{ message.content }}</div>
                <summary-card
                    v-if="message.summary"
                    :summary="message.summary"
                    :quote-id="message.summaryQuoteId"
                    @action="payload => $emit('action', payload)"
                ></summary-card>
            </div>
        </div>

        <!-- Tool call (collapsible) -->
        <div v-else-if="message.kind === 'tool'" class="ai-bubble-tool">
            <button
                type="button"
                class="ai-bubble-tool-toggle"
                :class="{ 'is-error': hasError }"
                @click="expanded = !expanded"
                :aria-expanded="expanded"
            >
                <span class="ai-bubble-tool-arrow">
                    <i class="fa" :class="expanded ? 'fa-caret-down' : 'fa-caret-right'"></i>
                </span>
                <span class="ai-bubble-tool-label">{{ humanLabel }}</span>
                <span v-if="paramHint" class="ai-bubble-tool-hint">{{ paramHint }}</span>
                <span v-if="hasError" class="ai-bubble-tool-badge">error</span>
            </button>
            <div v-if="expanded" class="ai-bubble-tool-detail">
                <div v-if="message.tool_params" class="ai-bubble-tool-section">
                    <div class="ai-bubble-tool-section-label">Parameters</div>
                    <pre>{{ formatJson(message.tool_params) }}</pre>
                </div>
                <div v-if="message.tool_result" class="ai-bubble-tool-section">
                    <div class="ai-bubble-tool-section-label">Result</div>
                    <pre>{{ formatJson(message.tool_result) }}</pre>
                </div>
                <div v-else class="ai-bubble-tool-section ai-bubble-tool-pending">
                    Waiting for result…
                </div>
            </div>
        </div>

    </div>
</template>

<script>
import SummaryCard from './SummaryCard.vue';

const TOOL_LABELS = {
    lookup_client: 'Looked up the client',
    get_client_history: 'Pulled client history',
    find_similar_items: 'Looked up similar past items',
    get_enquiry: 'Read the enquiry',
    list_bundles: 'Listed bundles',
    get_bundle: 'Loaded a bundle',
    list_segments: 'Listed segments',
    list_product_types: 'Listed product types',
    list_recent_enquiries: 'Listed recent enquiries',
    get_pricing_catalogue: 'Loaded the pricing catalogue',
    create_quote_draft: 'Created the quote',
    update_quote: 'Updated the quote',
    add_item_from_bundle: 'Added an item from a bundle',
    add_blank_item: 'Added a blank item',
    update_item_options: 'Updated item options',
    set_item_dimensions: 'Set item dimensions',
    recalculate_quote: 'Recalculated the quote',
    get_quote_total: 'Checked quote total',
    get_item_price_breakdown: 'Looked at price breakdown',
    suggest_cost_reductions: 'Looked for cost reductions',
    apply_alternative: 'Applied an alternative',
    compare_alternatives: 'Compared alternatives',
    summarise_quote: 'Summarised the draft',
    parse_enquiry_email: 'Parsed the enquiry email',
    classify_client_status: 'Classified the client',
    generate_item_description: 'Generated an item description',
    validate_dimensions: 'Validated dimensions',
    copy_quote: 'Copied an existing quote',
    split_tiered_quote: 'Split a tiered quote',
};

export default {
    components: { SummaryCard },

    props: {
        message: { type: Object, required: true },
    },

    data: () => ({ expanded: false }),

    computed: {
        // "[Image attached: ai_sessions/<uuid>/<file>]" markers written by the
        // upload endpoint — render as thumbnails, hide the marker text.
        attachedImages() {
            const c = this.message.content || '';
            const out = [];
            const re = /\[Image attached: (ai_sessions\/[A-Za-z0-9\-]+\/[A-Za-z0-9._-]+)\]/g;
            let m;
            while ((m = re.exec(c)) !== null) out.push('/uploads/' + m[1]);
            return out;
        },

        contentWithoutImageMarkers() {
            return (this.message.content || '')
                .replace(/\[Image attached: [^\]]+\]/g, '')
                .trim();
        },

        humanLabel() {
            return TOOL_LABELS[this.message.tool_name] || this.message.tool_name;
        },

        paramHint() {
            const p = this.message.tool_params;
            if (!p || typeof p !== 'object') return '';
            // One useful hint per common tool; falls back to nothing.
            const t = this.message.tool_name;
            if (t === 'lookup_client' && p.search) return p.search;
            if (t === 'get_client_history' && p.email) return p.email;
            if (t === 'get_enquiry' && p.enquiry_id) return '#' + p.enquiry_id;
            if (t === 'get_bundle' && p.bundle_id) return '#' + p.bundle_id;
            if (t === 'create_quote_draft' && p.name) return p.name;
            if ((t === 'add_item_from_bundle' || t === 'add_blank_item') && p.name) return p.name;
            if (t === 'set_item_dimensions' && p.width && p.height) return p.width + '×' + p.height + 'mm';
            if ((t === 'update_item_options' || t === 'apply_alternative') && p.item_id) {
                return 'item #' + p.item_id;
            }
            if (t === 'get_quote_total' && p.quote_id) return '#' + p.quote_id;
            if (t === 'summarise_quote' && p.quote_id) return '#' + p.quote_id;
            return '';
        },

        hasError() {
            const r = this.message.tool_result;
            return !!(r && typeof r === 'object' && r.error);
        },
    },

    methods: {
        formatJson(obj) {
            try {
                return JSON.stringify(obj, null, 2);
            } catch (e) {
                return String(obj);
            }
        },
    },
};
</script>

<style>
.ai-bubble {
    display: flex;
}

/* User */

.ai-bubble-image {
    display: block;
    max-width: 220px;
    max-height: 220px;
    border-radius: 8px;
    margin-bottom: 6px;
}

.ai-bubble-user {
    margin-left: auto;
    max-width: 80%;
}

.ai-bubble-user .ai-bubble-content {
    background: var(--ai-user-tint);
    color: var(--ai-text);
    padding: 10px 14px;
    border-radius: 16px 16px 4px 16px;
    font-size: 14px;
    line-height: 1.5;
    white-space: pre-wrap;
    word-wrap: break-word;
}

/* Assistant */

.ai-bubble-assistant {
    display: flex;
    gap: 10px;
    max-width: 92%;
}

.ai-bubble-avatar {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: linear-gradient(135deg, #2563eb, #7c3aed);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 12px;
    margin-top: 2px;
}

.ai-bubble-body {
    flex: 1;
    min-width: 0;
}

.ai-bubble-assistant .ai-bubble-content {
    color: var(--ai-text);
    font-size: 14px;
    line-height: 1.6;
    white-space: pre-wrap;
    word-wrap: break-word;
}

/* Tool calls */

.ai-bubble-tool {
    width: 100%;
    padding-left: 38px;
}

.ai-bubble-tool-toggle {
    background: none;
    border: none;
    padding: 2px 0;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: var(--ai-muted);
    font: inherit;
    font-size: 13px;
    text-align: left;
}

.ai-bubble-tool-toggle:hover {
    color: var(--ai-text);
}

.ai-bubble-tool-toggle.is-error {
    color: #b91c1c;
}

.ai-bubble-tool-arrow {
    width: 12px;
    display: inline-flex;
    justify-content: center;
    font-size: 11px;
}

.ai-bubble-tool-label {
    font-weight: 500;
}

.ai-bubble-tool-hint {
    color: var(--ai-muted);
    font-size: 12px;
}

.ai-bubble-tool-toggle.is-error .ai-bubble-tool-hint {
    color: #b91c1c;
    opacity: .7;
}

.ai-bubble-tool-badge {
    background: #fee2e2;
    color: #b91c1c;
    border-radius: 999px;
    padding: 1px 8px;
    font-size: 11px;
    font-weight: 500;
}

.ai-bubble-tool-detail {
    margin-top: 6px;
    padding: 10px 12px;
    background: var(--ai-tool-tint);
    border-radius: var(--ai-radius-sm);
    border: 1px solid var(--ai-border);
    font-size: 12px;
}

.ai-bubble-tool-section + .ai-bubble-tool-section {
    margin-top: 10px;
}

.ai-bubble-tool-section-label {
    color: var(--ai-muted);
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: .04em;
    margin-bottom: 4px;
    font-weight: 600;
}

.ai-bubble-tool-detail pre {
    background: #fff;
    border: 1px solid var(--ai-border);
    border-radius: 6px;
    padding: 8px 10px;
    font-size: 11.5px;
    line-height: 1.4;
    margin: 0;
    overflow-x: auto;
    max-height: 280px;
    overflow-y: auto;
    color: #0f172a;
    font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace;
}

.ai-bubble-tool-pending {
    font-style: italic;
    color: var(--ai-muted);
}
</style>
